import React, { useEffect, useMemo, useRef, useState } from "react"
import { Virtuoso } from 'react-virtuoso'
import styles from "./emojis.module.css"
import Emoji from "./Emoji"
import { MdAccessTime, MdOutlineEmojiEmotions, MdOutlineFlag, MdOutlineSearch, MdOutlineSportsSoccer, MdOutlineTravelExplore } from "react-icons/md"
import { TbBalloon, TbCategory, TbCircle, TbMoodSmile, TbPizza, TbShirt } from "react-icons/tb"
import { whatsAppEmojiGroups, whatsAppEmojiMap } from "./WhatsAppEmojiManifest"
import { whatsAppEmojiSprites, whatsAppEmojiSpriteMap } from "./WhatsAppEmojiSpriteManifest"
import { getWhatsAppEmojiSpriteAssetUrl } from "./whatsAppEmojiAssets"
import whatsAppEmojiKeywords from "./WhatsAppEmojiKeywords"
import { getRecentEmojiIcons, recentEmojisUpdatedEvent } from "./emojiRecents"
import Input from "../Form/Input"

const emojiMinColumnWidth = 42

const categoryIcons = {
    recentes: MdAccessTime,
    smileys: TbMoodSmile,
    natureza: TbBalloon,
    comidas: TbPizza,
    atividades: MdOutlineSportsSoccer,
    viagens: MdOutlineTravelExplore,
    objetos: TbShirt,
    simbolos: TbCircle,
    bandeiras: MdOutlineFlag,
    all: TbCategory,
}

const emojiCategories = whatsAppEmojiGroups.map(group => ({
    id: group.id,
    title: group.title,
    icon: categoryIcons[group.id] || TbCategory,
    list: group.emojis,
}))

const normalizeSearch = (value = "") => {
    return `${value}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
}

const getEmojiSearchText = (emoji, category) => {
    return normalizeSearch(`${emoji.icon} ${emoji.code} ${category.title} ${category.id} ${(whatsAppEmojiKeywords[emoji.icon] || []).join(" ")}`)
}

const getEmojiSprite = (code) => {
    const coordinates = whatsAppEmojiSpriteMap[code]
    const sprite = coordinates ? whatsAppEmojiSprites[coordinates.sprite] : null

    return sprite ? {
        ...sprite,
        url: getWhatsAppEmojiSpriteAssetUrl(sprite.file),
        x: coordinates.x,
        y: coordinates.y,
    } : null
}

const Emojis = ({ onClick = () => { }, accountId, userId }) => {
    const virtuosoRef = useRef(null)
    const listRef = useRef(null)
    const categoryClickTimeoutRef = useRef(null)
    const manualCategoryRef = useRef(false)
    const [activeCategoryId, setActiveCategoryId] = useState("smileys")
    const [search, setSearch] = useState("")
    const [recentEmojiIcons, setRecentEmojiIcons] = useState(() => getRecentEmojiIcons(accountId, userId))
    const [emojiColumns, setEmojiColumns] = useState(9)
    const normalizedSearch = normalizeSearch(search)

    useEffect(() => {
        const handleRecentEmojisUpdated = () => setRecentEmojiIcons(getRecentEmojiIcons(accountId, userId))
        handleRecentEmojisUpdated()

        window.addEventListener(recentEmojisUpdatedEvent, handleRecentEmojisUpdated)
        return () => window.removeEventListener(recentEmojisUpdatedEvent, handleRecentEmojisUpdated)
    }, [accountId, userId])

    useEffect(() => {
        const element = listRef.current
        if (!element) return

        const updateColumns = () => {
            const width = element.getBoundingClientRect().width
            const nextColumns = Math.max(1, Math.floor(width / emojiMinColumnWidth))
            setEmojiColumns(previousValue => previousValue === nextColumns ? previousValue : nextColumns)
        }

        updateColumns()

        if (typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", updateColumns)
            return () => window.removeEventListener("resize", updateColumns)
        }

        const observer = new ResizeObserver(updateColumns)
        observer.observe(element)

        return () => observer.disconnect()
    }, [])

    const categories = useMemo(() => {
        const recentList = recentEmojiIcons
            .map(icon => whatsAppEmojiMap[icon] ? { ...whatsAppEmojiMap[icon], icon } : null)
            .filter(Boolean)

        if (!recentList.length) return emojiCategories

        return [
            {
                id: "recentes",
                title: "Recentes",
                icon: categoryIcons.recentes,
                list: recentList,
            },
            ...emojiCategories,
        ]
    }, [recentEmojiIcons])

    const filteredCategories = useMemo(() => {
        if (!normalizedSearch) return categories

        return categories
            .map(category => ({
                ...category,
                list: category.list.filter(emoji => getEmojiSearchText(emoji, category).includes(normalizedSearch)),
            }))
            .filter(category => category.list.length > 0)
    }, [categories, normalizedSearch])

    const { rows, categoryIndexes } = useMemo(() => {
        const result = []
        const indexes = {}

        filteredCategories.forEach(category => {
            indexes[category.id] = result.length
            result.push({
                type: "header",
                category,
            })

            for (let i = 0; i < category.list.length; i += emojiColumns) {
                result.push({
                    type: "emojis",
                    category,
                    emojis: category.list.slice(i, i + emojiColumns),
                })
            }
        })

        return { rows: result, categoryIndexes: indexes }
    }, [filteredCategories, emojiColumns])

    const getCategoryIdByIndex = (index) => {
        for (let i = index; i >= 0; i--) {
            const row = rows[i]
            if (row?.type === "header" && row?.category?.id) {
                return row.category.id
            }
        }
        return rows[index]?.category?.id
    }

    const handleRangeChanged = ({ startIndex }) => {
        if (manualCategoryRef.current) {
            return
        }

        const visibleIndex = Math.min(rows.length - 1, startIndex)
        const categoryId = getCategoryIdByIndex(visibleIndex)
        if (categoryId && categoryId !== activeCategoryId) {
            setActiveCategoryId(categoryId)
        }
    }

    const handleClickCategory = (categoryId) => {
        const index = categoryIndexes[categoryId]
        if (index === undefined) return

        manualCategoryRef.current = true
        clearTimeout(categoryClickTimeoutRef.current)
        setActiveCategoryId(categoryId)
        virtuosoRef.current?.scrollToIndex({
            index,
            align: "start",
            behavior: "smooth",
        })
        categoryClickTimeoutRef.current = setTimeout(() => {
            manualCategoryRef.current = false
        }, 900)
    }

    const handleClickEmoji = (icon) => {
        onClick(icon)
    }

    return (
        <div className={styles.container} onClick={(e) => e.stopPropagation()} >
            <div className={styles.header}>
                <MdOutlineEmojiEmotions />
                <span>{categories.find(category => category.id === activeCategoryId)?.title || "Emojis"}</span>
            </div>
            <div className={styles.search}>
                <MdOutlineSearch />
                <Input
                    type="text"
                    value={search}
                    placeholder="Buscar emoji, categoria ou código..."
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault()
                        }
                    }}
                />
            </div>
            <div className={styles.list} ref={listRef}>
                <Virtuoso
                    ref={virtuosoRef}
                    style={{ height: '100%', width: '100%' }}
                    totalCount={rows.length}
                    increaseViewportBy={0}
                    rangeChanged={handleRangeChanged}
                    itemContent={(index) => {
                        const row = rows[index]

                        if (row?.type === "header") {
                            return <div className={styles.groupTitle}>{row.category.title}</div>
                        }

                        return (
                            <div className={styles.iconsRow} style={{ "--emoji-columns": emojiColumns }}>
                                {row?.emojis?.map((emoji) => (
                                    <Emoji
                                        key={`${emoji.code}-${emoji.icon}`}
                                        icon={emoji.icon}
                                        url={emoji.url}
                                        sprite={getEmojiSprite(emoji.code)}
                                        onClick={handleClickEmoji}
                                    />
                                ))}
                            </div>
                        )
                    }}
                />
            </div>
            <div className={styles.navigation}>
                {categories.map(category => {
                    const Icon = category.icon
                    return (
                        <button
                            key={category.id}
                            type="button"
                            className={category.id === activeCategoryId ? styles.active : ""}
                            title={category.title}
                            onClick={() => handleClickCategory(category.id)}
                            disabled={categoryIndexes[category.id] === undefined}
                        >
                            <Icon />
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

export default Emojis
